import { z } from "zod";

import type { operations } from "@/api/schema";

/** Mirrors components["schemas"]["GlobalAuditModule"] — kept as a runtime
 * literal list because the generated schema is types-only (erased at
 * runtime), same as the Sets in inventory/orders url-state. */
export const AUDIT_MODULES = [
  "inventory",
  "orders",
  "promotions",
  "users",
  "authentication",
  "integrations",
  "audit",
] as const;

/** Mirrors components["schemas"]["GlobalAuditResult"]. */
export const AUDIT_RESULTS = ["success", "partial", "failed"] as const;

/** What the "Needs attention" chip sends — the list endpoint's `result` takes
 * a comma-separated list (see api/openapi.yaml, listAuditEvents). */
const ATTENTION_RESULTS = "failed,partial";

/** Date-range presets shown in the UI. "custom" is the only one that reads an
 * explicit range from `date_from`/`date_to`; the others are always computed
 * relative to "now" at query-build time. */
export const DATE_PRESETS = ["24h", "7d", "30d", "custom"] as const;
export type DatePreset = (typeof DATE_PRESETS)[number];

const PRESET_DURATION_MS: Record<Exclude<DatePreset, "custom">, number> = {
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
};

const isoDateTime = z
  .string()
  .refine((v) => !Number.isNaN(Date.parse(v)), { message: "invalid ISO-8601 timestamp" });

/**
 * Zod schema = single source of validation + type for the audit log URL
 * state (CLAUDE.md rule 6). Body of the /audit-log route's `validateSearch`.
 * Invalid/unknown values fall back to defaults rather than throwing —
 * matches the inventory/orders url-state precedent, so a hand-edited or
 * stale URL never breaks the route.
 */
const auditSearchSchema = z.object({
  preset: z.enum(DATE_PRESETS).catch("7d"),
  // Only meaningful when preset === "custom"; ignored (recomputed from "now")
  // otherwise. See auditFilterToQuery.
  date_from: isoDateTime.optional().catch(undefined),
  date_to: isoDateTime.optional().catch(undefined),
  module: z.enum(AUDIT_MODULES).optional().catch(undefined),
  activity: z.string().trim().min(1).optional().catch(undefined),
  actor: z.string().trim().min(1).optional().catch(undefined),
  result: z.enum(AUDIT_RESULTS).optional().catch(undefined),
  // "Needs attention" chip — shorthand for result=failed,partial. Kept as its
  // own flag rather than stuffing a comma list into `result` so the Result
  // dropdown stays a single-value control and the chip stays togglable.
  attention: z.boolean().optional().catch(undefined),
  // Object-centric timeline: "order:<id>" | "sku:<sino_sku>". Set by the
  // drawer's "Timeline for this order/SKU", not typed by hand.
  related_to: z
    .string()
    .regex(/^(order|sku):.+$/)
    .optional()
    .catch(undefined),
  // Exact correlation ID, or partial target ID/label/actor email — the same
  // free-text field the backend's `search` query param matches against.
  search: z.string().trim().min(1).optional().catch(undefined),
  // Exact-match target filters — set by a deep link from another feature
  // (e.g. an order detail page's "View all in Audit log"), not exposed as a
  // free-text filter in AuditFilters itself.
  target_type: z.string().trim().min(1).optional().catch(undefined),
  target_id: z.string().trim().min(1).optional().catch(undefined),
  offset: z.coerce.number().int().nonnegative().catch(0),
  // UI-only: which row's detail drawer is open. Deliberately excluded from
  // auditFilterToQuery — never sent to the list/export API.
  event_id: z.coerce.number().int().positive().optional().catch(undefined),
});

export type AuditFilterState = z.infer<typeof auditSearchSchema>;

/**
 * Parses TanStack Router's raw search record into a typed AuditFilterState.
 * Used as the audit-log route's `validateSearch` body.
 */
export function parseAuditSearch(raw: Record<string, string | undefined>): AuditFilterState {
  return auditSearchSchema.parse(raw);
}

/** Fixed page size for the audit list/export APIs — not user-configurable, so
 * it lives here rather than in AuditFilterState. */
const AUDIT_QUERY_LIMIT = 50;

export type AuditEventQuery = NonNullable<operations["listAuditEvents"]["parameters"]["query"]>;

/**
 * Builds the query object sent to the list (and, minus offset/limit, export)
 * audit API from URL filter state. Relative presets are resolved fresh
 * against `now` (so "last 24h" always means the last 24h as of now, not a
 * stale window from when a link was created); "custom" passes its stored
 * range through verbatim so a deep link reproduces the exact bounds it was
 * given. `event_id` is UI-only and is deliberately never included here.
 */
export function auditFilterToQuery(
  filter: AuditFilterState,
  now: Date = new Date(),
): AuditEventQuery {
  const { date_from, date_to } = resolveDateRange(filter, now);
  // Grouping is always on: a warehouse retry loop emits the same failure every
  // tick, and 52 identical rows/minute bury everything else. Success events are
  // never grouped by the backend, so this is free for the rest of the ledger.
  return {
    date_from,
    date_to,
    offset: filter.offset,
    limit: AUDIT_QUERY_LIMIT,
    group: true,
    ...(filter.module !== undefined && { module: filter.module }),
    ...(filter.activity !== undefined && { activity: filter.activity }),
    ...(filter.actor !== undefined && { actor: filter.actor }),
    ...(filter.attention === true
      ? { result: ATTENTION_RESULTS }
      : filter.result !== undefined && { result: filter.result }),
    ...(filter.related_to !== undefined && { related_to: filter.related_to }),
    ...(filter.search !== undefined && { search: filter.search }),
    ...(filter.target_type !== undefined && { target_type: filter.target_type }),
    ...(filter.target_id !== undefined && { target_id: filter.target_id }),
  };
}

function resolveDateRange(
  filter: AuditFilterState,
  now: Date,
): { date_from: string; date_to: string } {
  if (
    filter.preset === "custom" &&
    filter.date_from !== undefined &&
    filter.date_to !== undefined
  ) {
    return { date_from: filter.date_from, date_to: filter.date_to };
  }
  // "custom" with a missing range (shouldn't normally happen once Task 3's UI
  // requires both dates together) falls back to the same 7-day default as a
  // brand-new visit, rather than sending an invalid/incomplete query.
  const preset = filter.preset === "custom" ? "7d" : filter.preset;
  const spanMs = PRESET_DURATION_MS[preset];
  return { date_from: new Date(now.getTime() - spanMs).toISOString(), date_to: now.toISOString() };
}

/** Returns the filter with offset cleared — call when any non-paging filter
 * changes so results jump back to page 1 (mirrors orders' resetPage). */
export function resetPage(filter: AuditFilterState): AuditFilterState {
  return { ...filter, offset: 0 };
}
